import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BookOpenIcon, CheckCircleIcon } from 'lucide-react';
const NAMES = ['María José', 'Carmen', 'Lucía', 'Patricia', 'Isabel', 'Mónica', 'Beatriz', 'Laura', 'Cristina', 'Andrea', 'Elena', 'Sofía', 'Adriana', 'Verónica', 'Gabriela', 'Rocío', 'Pilar', 'Silvia', 'Raquel', 'Teresa', 'Claudia', 'Marta', 'Susana', 'Alejandra', 'Natalia', 'Marisol', 'Esperanza', 'Yolanda', 'Mercedes', 'Concepción', 'Rosario', 'Daniela', 'Valeria', 'Liliana', 'Paola'];
const CITIES = ['Madrid, España', 'Barcelona, España', 'Buenos Aires, Argentina', 'Ciudad de México, México', 'Guadalajara, México', 'Monterrey, México', 'Miami, USA', 'Los Ángeles, USA', 'Bogotá, Colombia', 'Medellín, Colombia', 'Lima, Perú', 'Santiago, Chile', 'Quito, Ecuador', 'San José, Costa Rica', 'Caracas, Venezuela', 'Montevideo, Uruguay', 'Asunción, Paraguay', 'San Juan, Puerto Rico', 'Valencia, España', 'Sevilla, España', 'Cancún, México', 'Houston, USA'];
const PRODUCTS = [{
  name: 'Código Hormonal 40+',
  price: '$7.39'
}, {
  name: 'Código Hormonal 40+ Edición Completa',
  price: '$7.39'
}];
const HISTORY_LIMIT = 10;
const MIN_INTERVAL = 9000;
const MAX_INTERVAL = 18000;
const DISPLAY_DURATION = 4000;
const INITIAL_DELAY = 5000;
type Notification = {
  id: number;
  name: string;
  city: string;
  product: string;
  price: string;
  timeAgo: string;
};
const TIME_AGO_OPTIONS = ['hace unos segundos', 'hace 1 minuto', 'hace 2 minutos', 'hace 5 minutos', 'hace 8 minutos', 'hace 12 minutos'];
function pickRandom<T>(arr: T[], avoid: T[] = []): T {
  const available = arr.filter(item => !avoid.includes(item));
  const pool = available.length > 0 ? available : arr;
  return pool[Math.floor(Math.random() * pool.length)];
}
export function PurchaseNotifications() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const historyRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    let displayTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    let isMounted = true;
    const showNext = () => {
      if (!isMounted) return;
      const name = pickRandom(NAMES, historyRef.current);
      const city = pickRandom(CITIES);
      const product = pickRandom(PRODUCTS);
      const timeAgo = pickRandom(TIME_AGO_OPTIONS);
      historyRef.current = [name, ...historyRef.current].slice(0, HISTORY_LIMIT);
      counterRef.current += 1;
      setCurrent({
        id: counterRef.current,
        name,
        city,
        product: product.name,
        price: product.price,
        timeAgo
      });
      displayTimer = setTimeout(() => {
        if (!isMounted) return;
        setCurrent(null);
        const nextDelay = MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
        nextTimer = setTimeout(showNext, nextDelay);
      }, DISPLAY_DURATION);
    };
    nextTimer = setTimeout(showNext, INITIAL_DELAY);
    return () => {
      isMounted = false;
      clearTimeout(displayTimer);
      clearTimeout(nextTimer);
    };
  }, []);
  const enterAnimation = reduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0
  };
  const initialAnimation = reduceMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 30
  };
  const exitAnimation = reduceMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 30
  };
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-4 left-4 right-4 sm:right-auto sm:bottom-6 sm:left-6 z-40 pointer-events-none" data-id="element-156">
      <AnimatePresence data-id="element-157">
        {current && <motion.div key={current.id} initial={initialAnimation} animate={enterAnimation} exit={exitAnimation} transition={{
        duration: reduceMotion ? 0 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-warm-white rounded-xl shadow-[0_10px_40px_-5px_rgba(45,74,62,0.25),0_4px_15px_-3px_rgba(0,0,0,0.08)] border border-charcoal/5 p-3 sm:p-4 max-w-sm flex items-center gap-3" role="status" data-id="element-158">
            <div className="relative flex-shrink-0" data-id="element-159">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-forest flex items-center justify-center" data-id="element-160">
                <BookOpenIcon className="w-6 h-6 sm:w-7 sm:h-7 text-gold-light" data-id="element-161" />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-warm-white rounded-full p-0.5" data-id="element-162">
                <CheckCircleIcon className="w-4 h-4 text-forest fill-forest" strokeWidth={2.5} stroke="#FFFCF5" data-id="element-163" />
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-164">
              <p className="text-charcoal text-sm leading-snug" data-id="element-165">
                <span className="font-semibold" data-id="element-166">{current.name}</span> en{' '}
                <span className="font-medium" data-id="element-167">{current.city}</span>
              </p>
              <p className="text-charcoal/70 text-xs sm:text-sm leading-snug mt-0.5 truncate" data-id="element-168">
                acaba de comprar {current.product}
              </p>
              <p className="text-charcoal/50 text-xs mt-1 flex items-center gap-1.5" data-id="element-169">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-forest animate-pulse" aria-hidden="true" data-id="element-170"></span>
                {current.timeAgo} · {current.price}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}